import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Warning, Copy, Key } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-factor set-up, required before a staff account gets a session at all.
 *
 * It runs against the short-lived token the password already earned, not a
 * session — because handing out a session first would defeat the point. The
 * superadmin reads every support thread as an ordinary participant of each
 * chat rather than through an admin route, so a stolen password would open
 * every conversation while the account waited to be protected.
 *
 * The recovery codes are the reason this has three steps instead of two. They
 * are readable exactly once, and a screen you can leave by accident is not
 * where you put something that cannot be shown again.
 */
const StaffEnrollment = ({ onDone, onCancel }) => {
  const [step, setStep] = useState('scan');   // scan | codes
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState([]);
  const [written, setWritten] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const begin = useCallback(async () => {
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/enroll/setup`, {}, { withCredentials: true },
      );
      setQr(data.qr_data_uri);
      setSecret(data.secret);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start set-up. Sign in again.');
    }
  }, []);

  useEffect(() => { begin(); }, [begin]);

  const confirm = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/enroll/confirm`, { code: code.trim() },
        { withCredentials: true },
      );
      setRecovery(data.recovery_codes || []);
      setStep('codes');
    } catch (err) {
      setError(err.response?.data?.detail || 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(recovery.join('\n')); } catch (e) { /* denied */ }
  };

  if (step === 'codes') {
    return (
      <div className="space-y-4" data-testid="enroll-recovery">
        <div className="text-center">
          <Key className="w-9 h-9 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
          <h2 className="font-heading text-xl text-foreground">Your recovery codes</h2>
        </div>

        <div className="flex items-start gap-2 text-sm bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <Warning className="w-4 h-4 mt-0.5 shrink-0 text-destructive" weight="fill" />
          <span className="text-destructive">
            This is the only time these are shown. Lose your authenticator
            without them and nobody can let you back in.
          </span>
        </div>

        <div
          className="grid grid-cols-2 gap-2 font-mono text-sm bg-muted/40 border border-border rounded-lg p-3"
          data-testid="enroll-codes"
        >
          {recovery.map((c) => (
            <span key={c} className="tabular-nums" style={{ overflowWrap: 'anywhere' }}>{c}</span>
          ))}
        </div>

        <Button type="button" variant="outline" onClick={copyAll} className="w-full">
          <Copy className="w-4 h-4 mr-2" /> Copy them
        </Button>

        <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={written}
            onChange={(e) => setWritten(e.target.checked)}
            className="mt-1"
            data-testid="enroll-written"
          />
          <span>I have stored these somewhere other than this phone.</span>
        </label>

        <Button
          type="button"
          disabled={!written}
          onClick={onDone}
          className="w-full h-11 bg-primary hover:bg-primary/90"
          data-testid="enroll-finish"
        >
          Enter Arcanum
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={confirm} className="space-y-4" data-testid="enroll-scan">
      <div className="text-center">
        <ShieldCheck className="w-9 h-9 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
        <h2 className="font-heading text-xl text-foreground">Set up two-factor</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Required for staff. This account can read every support thread, so a
          password on its own is not enough to hold it.
        </p>
      </div>

      {qr ? (
        <div className="flex flex-col items-center gap-3">
          <img
            src={qr}
            alt="Scan this with your authenticator app"
            className="w-48 h-48 rounded-lg bg-white p-2"
            data-testid="enroll-qr"
          />
          {/* Scanning has to happen INSIDE an authenticator app — a camera app
              will not do it, which has confused people here before. */}
          <p className="text-xs text-muted-foreground text-center">
            Scan it from inside your authenticator app, not the camera. Or enter
            this key by hand:
          </p>
          <code
            className="text-xs bg-muted/50 border border-border rounded px-2 py-1 select-all"
            style={{ overflowWrap: 'anywhere' }}
          >
            {secret}
          </code>
        </div>
      ) : error ? (
        /* Not "Preparing…" forever. A screen that looks busy while it has in
           fact given up is how somebody concludes the app is broken and stops.
           The server says how long any wait is; say it here and offer the
           retry, so nobody has to guess that reloading is the answer. */
        <div className="text-center py-6 space-y-3" data-testid="enroll-setup-failed">
          <Warning className="w-7 h-7 mx-auto text-destructive" weight="fill" />
          <p className="text-sm text-destructive px-2">{error}</p>
          <Button type="button" variant="outline" onClick={begin} className="w-full">
            Try again
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">Preparing…</p>
      )}

      <div className="space-y-2">
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="Enter your code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          className="h-11 text-center tracking-[0.3em] placeholder:tracking-normal placeholder:text-sm"
          data-testid="enroll-code"
        />
      </div>

      {error && qr && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" />
          <span data-testid="enroll-error">{error}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={busy || !qr || code.trim().length < 6}
        className="w-full h-11 bg-primary hover:bg-primary/90"
        data-testid="enroll-submit"
      >
        {busy ? 'Confirming…' : 'Confirm and continue'}
      </Button>

      <button
        type="button"
        onClick={onCancel}
        className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        Back
      </button>
    </form>
  );
};

export default StaffEnrollment;
